// Development Studies Curriculum Data - Years 11-13
// Based on Vanuatu National Curriculum

export const developmentStudiesCurriculum = {
  subject: "Development Studies",
  years: "11-13",
  totalHours: {
    year11: 132,
    year12: 165,
    year13: 165
  },
  rationale: `Development Studies is a multi-disciplinary subject that examines social change in the modern world, with special emphasis on developments that affect the quality of life in developing countries. The course prepares students to understand real-life issues, participate in community development, and make informed decisions about sustainable development for Vanuatu and the Pacific region.`,
  
  strands: [
    {
      id: "DST1",
      name: "What is Development?",
      description: "Understanding the term 'development', its levels, indicators, and inequalities at local, regional and world scales"
    },
    {
      id: "DST2",
      name: "Economic Development",
      description: "Economic systems, sectors of development, agriculture, industry, employment and their impacts on quality of life"
    },
    {
      id: "DST3",
      name: "Promoting Development",
      description: "Factors that promote or hinder development: aid, technology, organizations, and community involvement"
    },
    {
      id: "DST4",
      name: "Development and Environment",
      description: "Natural environment, climate change, natural resources, and environmental sustainability"
    },
    {
      id: "DST5",
      name: "Social, Cultural, Spiritual and Political Development",
      description: "Human societies, migration, population, diversity, governance, health, education and human rights"
    }
  ],
  
  assessment: {
    year11: {
      internal: { weighting: 60, description: "Internal Assessments across all strands" },
      external: { weighting: 40, description: "Year 11 External Examination" }
    },
    year12: {
      internal: { weighting: 40, description: "Quality of Life Survey, Seminars, Research Projects" },
      external: { weighting: 60, description: "Year 12 External Examination" }
    },
    year13: {
      internal: { weighting: 40, description: "Development Plan Project, Case Studies, Presentations" },
      external: { weighting: 60, description: "VSSC Final Examination" }
    }
  },

  years: {
    year11: {
      hours: 132,
      hoursPerWeek: 4,
      focus: "Introduction to development concepts, indicators, economic systems, environment basics, and human societies",
      strandWeighting: {
        DST1: { hours: 16, weight: 12 },
        DST2: { hours: 16, weight: 12 },
        DST3: { hours: 24, weight: 18 },
        DST4: { hours: 38, weight: 29 },
        DST5: { hours: 38, weight: 29 }
      },
      units: [
        {
          strand: "DST1",
          name: "Nature and Aspects of Development",
          objectives: [
            "Define the term 'development'",
            "List and arrange the different aspects of development",
            "Explain why development does not always mean economic development",
            "Interpret cartoons and pictures that show the meaning of development"
          ],
          content: [
            "Definition of development",
            "Aspects: economic, social, political, cultural, environmental, spiritual",
            "The star diagram of development aspects",
            "Development vs economic growth"
          ],
          activities: [
            "Creating star diagrams of development aspects",
            "Analyzing cartoons about development",
            "Class discussions on what development means"
          ]
        },
        {
          strand: "DST1",
          name: "Development Indicators",
          objectives: [
            "Name indicators for measuring economic, social, environmental development",
            "Define GNP, GDP, and Purchasing Power Parity (PPP)",
            "Name indicators combined in Human Development Index",
            "Explain usefulness and limitations of statistical indicators"
          ],
          content: [
            "GDP and GNP calculations",
            "Human Development Index (HDI)",
            "Infant mortality rate, literacy rate, life expectancy",
            "Qualitative vs quantitative indicators"
          ],
          activities: [
            "Calculating GDP per capita",
            "Comparing HDI of different countries",
            "Discussing limitations of indicators"
          ]
        },
        {
          strand: "DST1",
          name: "Millennium Development Goals",
          objectives: [
            "Define Millennium Development Goals (now SDGs)",
            "List the goals and their importance",
            "Explain why Vanuatu should attain these goals"
          ],
          content: [
            "The 8 MDGs and transition to 17 SDGs",
            "Vanuatu's progress on development goals",
            "Global partnerships for development"
          ],
          activities: [
            "Researching Vanuatu's SDG progress",
            "Creating posters on development goals"
          ]
        },
        {
          strand: "DST2",
          name: "Economic Systems and Development",
          objectives: [
            "Define economic development and economic systems",
            "Identify different economic systems in Vanuatu",
            "Describe usefulness of subsistence and commercial systems"
          ],
          content: [
            "Subsistence economy",
            "Commercial economy",
            "Mixed economy in Vanuatu",
            "Traditional vs modern economic activities"
          ],
          activities: [
            "Comparing economic systems",
            "Case studies of Vanuatu communities"
          ]
        },
        {
          strand: "DST2",
          name: "Sectors of Economic Development",
          objectives: [
            "Name the four sectors of economic development",
            "Describe examples of activities in each sector",
            "Explain importance of all sectors for Vanuatu"
          ],
          content: [
            "Primary sector (agriculture, fishing, mining)",
            "Secondary sector (manufacturing, processing)",
            "Tertiary sector (services, trade)",
            "Quaternary sector (knowledge, technology)"
          ],
          activities: [
            "Classifying economic activities by sector",
            "Analyzing Vanuatu's economic structure"
          ]
        },
        {
          strand: "DST2",
          name: "Commercial and Subsistence Agriculture",
          objectives: [
            "Differentiate between farming types",
            "Draw agricultural system flow charts",
            "Explain why traditional practices should be maintained"
          ],
          content: [
            "Subsistence vs commercial farming",
            "Plantation agriculture in tropical areas",
            "Agricultural inputs, processes, outputs",
            "Food crops vs cash crops"
          ],
          activities: [
            "Creating agricultural system diagrams",
            "Comparing farming types in Vanuatu"
          ]
        },
        {
          strand: "DST3",
          name: "International Aid",
          objectives: [
            "Define international aid, ODA, donor, recipient",
            "Identify main sources and forms of aid",
            "Discuss advantages and disadvantages of foreign aid"
          ],
          content: [
            "Types of aid: grants, loans, technical assistance",
            "Major donors in Pacific: Australia, NZ, Japan, EU",
            "NGO aid vs government aid",
            "Aid dependency issues"
          ],
          activities: [
            "Researching aid projects in Vanuatu",
            "Debating pros and cons of foreign aid"
          ]
        },
        {
          strand: "DST3",
          name: "Major Technological Changes",
          objectives: [
            "Identify major technological changes throughout history",
            "Describe impacts on society",
            "Explain inequalities caused by technology"
          ],
          content: [
            "Agricultural revolution",
            "Industrial revolution",
            "Information technology revolution",
            "Digital divide"
          ],
          activities: [
            "Creating timelines of technological change",
            "Discussing technology's impact on Vanuatu"
          ]
        },
        {
          strand: "DST4",
          name: "The Earth in Space",
          objectives: [
            "Name planets in our solar system",
            "Explain Earth's unique features",
            "Describe Earth's rotation and revolution"
          ],
          content: [
            "Solar system structure",
            "Earth's unique characteristics",
            "Day and night, seasons",
            "Geographic coordinates"
          ],
          activities: [
            "Drawing solar system diagrams",
            "Explaining seasons and time zones"
          ]
        },
        {
          strand: "DST4",
          name: "The Earth's Natural Environment",
          objectives: [
            "Identify relief features of continents and oceans",
            "Describe atmosphere and climate types",
            "Explain greenhouse effect and global warming"
          ],
          content: [
            "Continental and oceanic features",
            "Climate zones: tropical, temperate, arctic",
            "Greenhouse gases and climate change",
            "Biomes: forests, grasslands, deserts"
          ],
          activities: [
            "Mapping climate zones",
            "Discussing climate change impacts"
          ]
        },
        {
          strand: "DST4",
          name: "Natural Resources",
          objectives: [
            "Define natural resources",
            "Classify resources as renewable and non-renewable",
            "Discuss sustainable resource use in Vanuatu"
          ],
          content: [
            "Types of natural resources",
            "Energy resources in Vanuatu",
            "Sustainable vs unsustainable use",
            "Traditional conservation measures"
          ],
          activities: [
            "Resource classification exercises",
            "Analyzing resource use in communities"
          ]
        },
        {
          strand: "DST5",
          name: "Development of Human Societies",
          objectives: [
            "Identify features of pre-industrial societies",
            "Draw timelines of human society evolution",
            "Explain how culture leads to inequalities"
          ],
          content: [
            "Hunter-gatherer societies",
            "Agricultural societies",
            "Industrial and post-industrial societies",
            "Traditional Vanuatu society"
          ],
          activities: [
            "Creating society evolution timelines",
            "Comparing traditional and modern societies"
          ]
        },
        {
          strand: "DST5",
          name: "Migration and Language",
          objectives: [
            "Give examples of human migrations",
            "List major world languages and distribution",
            "Explain why Vanuatu has many languages"
          ],
          content: [
            "Pacific migration patterns",
            "Language spread and distribution",
            "Vanuatu's linguistic diversity",
            "Indigenous language conservation"
          ],
          activities: [
            "Mapping Pacific migrations",
            "Discussing language preservation"
          ]
        },
        {
          strand: "DST5",
          name: "Population Growth",
          objectives: [
            "Locate world's major population clusters",
            "Describe effects of rapid population growth",
            "Propose measures to manage population growth"
          ],
          content: [
            "World population distribution",
            "Population growth rates",
            "Effects on development",
            "Population policies"
          ],
          activities: [
            "Analyzing population data",
            "Discussing population challenges"
          ]
        }
      ]
    },
    year12: {
      hours: 165,
      hoursPerWeek: 5,
      focus: "Deeper analysis of development indicators, economic sectors, food security, industrialization, and global issues",
      strandWeighting: {
        DST1: { hours: 30, weight: 23 },
        DST2: { hours: 45, weight: 29 },
        DST3: { hours: 25, weight: 18 },
        DST4: { hours: 20, weight: 15 },
        DST5: { hours: 20, weight: 15 }
      },
      units: [
        {
          strand: "DST1",
          name: "Aspects of Development (Review)",
          objectives: [
            "Define all aspects of development",
            "Evaluate effects on individuals and societies"
          ],
          content: [
            "Economic, social, political development",
            "Cultural, environmental, spiritual development",
            "Interrelationships between aspects"
          ],
          activities: [
            "Creating development aspect diagrams",
            "Case study analysis"
          ]
        },
        {
          strand: "DST1",
          name: "Quality of Life Survey",
          objectives: [
            "State purpose of Quality of Life survey",
            "Interview household members",
            "Present findings using graphs and tables",
            "Produce a quality of life survey report"
          ],
          content: [
            "Survey design and methodology",
            "Data collection techniques",
            "Data presentation methods",
            "Report writing"
          ],
          activities: [
            "Conducting community surveys",
            "Creating graphs and charts",
            "Writing survey reports"
          ]
        },
        {
          strand: "DST1",
          name: "Inequalities in Development",
          objectives: [
            "Define inequality and development gap",
            "Describe pattern of world trade",
            "Explain why poorer countries don't benefit from trade",
            "Recommend strategies for reducing inequalities"
          ],
          content: [
            "Development gap between nations",
            "Colonialism's impact on trade patterns",
            "Terms: MDC, LDC, MEDC, LEDC, NIC",
            "Fair trade and trade justice"
          ],
          activities: [
            "Analyzing world trade patterns",
            "Debating trade inequality"
          ]
        },
        {
          strand: "DST2",
          name: "Indicators of Economic Development",
          objectives: [
            "Define GDP, GDP per capita, PPP, HDI",
            "Calculate GDP and HDI",
            "Discuss limitations of economic indicators"
          ],
          content: [
            "GDP calculation methods",
            "Human Development Index components",
            "Limitations of GDP as welfare measure",
            "Alternative development indicators"
          ],
          activities: [
            "Calculating and comparing indicators",
            "Analyzing country development profiles"
          ]
        },
        {
          strand: "DST2",
          name: "Agriculture, Food Security and Poverty",
          objectives: [
            "Define poverty, hunger, malnutrition, food security",
            "Investigate agricultural systems in Vanuatu",
            "Evaluate effects of climate change on agriculture",
            "Advocate for sustainable agricultural techniques"
          ],
          content: [
            "Food security challenges",
            "Causes of poverty and hunger",
            "Climate change impacts on farming",
            "Sustainable agriculture solutions"
          ],
          activities: [
            "Food security seminars",
            "Analyzing climate change effects"
          ]
        },
        {
          strand: "DST2",
          name: "Secondary, Tertiary and Quaternary Production",
          objectives: [
            "Define industrialization and related terms",
            "Describe industrial revolution features",
            "Identify multinational companies in Vanuatu",
            "Evaluate importance of small enterprises and micro-finance"
          ],
          content: [
            "Industrialization strategies",
            "Multinational corporations",
            "Small-scale enterprises",
            "Micro-finance in Vanuatu"
          ],
          activities: [
            "Researching businesses in Vanuatu",
            "Analyzing foreign investment"
          ]
        },
        {
          strand: "DST3",
          name: "Promoting Rural Development",
          objectives: [
            "Define rural development",
            "List obstacles to rural development",
            "Recommend strategies for rural areas"
          ],
          content: [
            "Rural vs urban development",
            "Infrastructure challenges",
            "Community-based development",
            "Bottom-up development approaches"
          ],
          activities: [
            "Case studies of rural projects",
            "Designing development plans"
          ]
        },
        {
          strand: "DST4",
          name: "Climate Change",
          objectives: [
            "Define climate change and global warming",
            "Describe causes and effects",
            "Explain impacts on Pacific Islands"
          ],
          content: [
            "Greenhouse effect mechanism",
            "Sea level rise",
            "Extreme weather events",
            "Vanuatu's vulnerability"
          ],
          activities: [
            "Climate change research projects",
            "Discussing adaptation strategies"
          ]
        },
        {
          strand: "DST5",
          name: "Health and Development",
          objectives: [
            "Identify major health challenges",
            "Describe disease prevention strategies",
            "Explain importance of health care access"
          ],
          content: [
            "Health indicators",
            "Common diseases in developing countries",
            "Healthcare systems",
            "Health and development relationship"
          ],
          activities: [
            "Analyzing health data",
            "Discussing health challenges in Vanuatu"
          ]
        }
      ]
    },
    year13: {
      hours: 165,
      hoursPerWeek: 5,
      focus: "Advanced theories of development, development planning, global issues, governance and preparing for tertiary studies",
      strandWeighting: {
        DST1: { hours: 20, weight: 16 },
        DST2: { hours: 35, weight: 25 },
        DST3: { hours: 15, weight: 14 },
        DST4: { hours: 20, weight: 16 },
        DST5: { hours: 40, weight: 29 }
      },
      units: [
        {
          strand: "DST1",
          name: "Theories of Development",
          objectives: [
            "Understand modernization theory",
            "Understand core-periphery (dependency) theory",
            "Compare and evaluate development theories"
          ],
          content: [
            "Modernization theory (Rostow's stages)",
            "Dependency/core-periphery theory",
            "Alternative development approaches",
            "Sustainable development theory"
          ],
          activities: [
            "Comparing development theories",
            "Applying theories to Vanuatu"
          ]
        },
        {
          strand: "DST1",
          name: "Questioning Development",
          objectives: [
            "Critically analyze development concepts",
            "Evaluate different development approaches",
            "Propose alternative development paths"
          ],
          content: [
            "Critiques of Western development model",
            "Indigenous development approaches",
            "Sustainable development",
            "Human-centered development"
          ],
          activities: [
            "Critical analysis essays",
            "Debates on development approaches"
          ]
        },
        {
          strand: "DST2",
          name: "Globalization",
          objectives: [
            "Define globalization",
            "Describe positive and negative effects",
            "Analyze impact on Pacific Islands"
          ],
          content: [
            "Economic globalization",
            "Cultural globalization",
            "Winners and losers",
            "Globalization in the Pacific"
          ],
          activities: [
            "Globalization impact analysis",
            "Case studies of global trade"
          ]
        },
        {
          strand: "DST2",
          name: "World Trade and Finance",
          objectives: [
            "Describe international trade system",
            "Explain role of WTO, IMF, World Bank",
            "Analyze trade's impact on developing countries"
          ],
          content: [
            "International trade organizations",
            "Trade agreements",
            "Structural adjustment programs",
            "Debt issues"
          ],
          activities: [
            "Researching trade organizations",
            "Analyzing trade data"
          ]
        },
        {
          strand: "DST3",
          name: "Development Planning",
          objectives: [
            "Understand development planning process",
            "Design a local development plan",
            "Evaluate development projects"
          ],
          content: [
            "Planning methodologies",
            "Community participation",
            "Project evaluation criteria",
            "Sustainable project design"
          ],
          activities: [
            "Creating development plans",
            "Project evaluation exercises"
          ]
        },
        {
          strand: "DST4",
          name: "Environmental Security",
          objectives: [
            "Define environmental security",
            "Analyze threats to environment",
            "Propose solutions for sustainability"
          ],
          content: [
            "Environmental threats",
            "Conservation strategies",
            "International environmental agreements",
            "Vanuatu's environmental challenges"
          ],
          activities: [
            "Environmental impact assessments",
            "Conservation project design"
          ]
        },
        {
          strand: "DST4",
          name: "Natural Disasters",
          objectives: [
            "Identify types of natural disasters",
            "Describe disaster risk reduction",
            "Explain disaster management in Vanuatu"
          ],
          content: [
            "Cyclones, earthquakes, tsunamis, volcanoes",
            "Disaster preparedness",
            "NDMO role in Vanuatu",
            "Climate change and disasters"
          ],
          activities: [
            "Disaster preparedness plans",
            "Analyzing disaster case studies"
          ]
        },
        {
          strand: "DST5",
          name: "Political Development and Governance",
          objectives: [
            "Define democracy and good governance",
            "Describe Vanuatu's political system",
            "Analyze governance challenges"
          ],
          content: [
            "Democratic principles",
            "Vanuatu's government structure",
            "Good governance indicators",
            "Corruption and transparency"
          ],
          activities: [
            "Analyzing governance issues",
            "Comparing political systems"
          ]
        },
        {
          strand: "DST5",
          name: "Human Rights and Security",
          objectives: [
            "Define human rights and human security",
            "Identify human rights issues",
            "Explain importance of rights-based development"
          ],
          content: [
            "Universal Declaration of Human Rights",
            "Gender equality and women's rights",
            "Children's rights",
            "Human security dimensions"
          ],
          activities: [
            "Human rights case studies",
            "Rights-based development analysis"
          ]
        },
        {
          strand: "DST5",
          name: "Education and Development",
          objectives: [
            "Explain relationship between education and development",
            "Analyze education challenges in developing countries",
            "Propose strategies for improving education"
          ],
          content: [
            "Education as human capital",
            "Literacy and development",
            "Education access and quality",
            "Vanuatu's education system"
          ],
          activities: [
            "Education data analysis",
            "Comparing education systems"
          ]
        }
      ]
    }
  }
};

export default developmentStudiesCurriculum;
